import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Box, Container, Typography, Button, Stack, Chip, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import HyperspeedBg from './HyperspeedBg';
import VoiceWave from './VoiceWave';
import backendSystemSVG from '../../assets/images/backend-system.svg';
import backendSystemLeftSVG from '../../assets/images/backend-system-left-side.svg';
import backendSystemRightSVG from '../../assets/images/backend-system-right-side.svg';
import backendSystemPortraitSVG from '../../assets/images/backend-system-portrait.svg';
import AnimatedMeshBg from './AnimatedMeshBg';
import BackendSystemLines from './BackendSystemLines';
import BackendVisualizationPanel from './BackendVisualizationPanel';
import useOrientation from '../../hooks/useOrientation';

// Icons
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import CampaignIcon from '@mui/icons-material/Campaign';
import SmartToyIcon from '@mui/icons-material/SmartToy';

const MotionBox = motion(Box);


// Landscape mode system lines - LEFT SIDE
const systemLinesLeft = [
  { 
    points: [
      { x: 0.56, y: 0.30 },
      { x: 0.56, y: 0.55 },
      { x: 0.77, y: 0.645 },
      { x: 0.94, y: 0.56 },
      { x: 0.7, y: 0.46 },


    ]
  },
  {
    points: [ 
      { x: 0.79, y: 0.48 },
      { x: 0.66 ,y: 0.545 },  //line 4 cloud
      { x: 0.615, y: 0.525 },
      { x: 0.615, y: 0.30 },
    ]
   },
  {
    points: [ 
      { x: 0.51, y: 0.30 },
      { x: 0.51, y: 0.58 },
      { x: 0.77, y: 0.7 },
      { x: 0.93, y: 0.62 },
    ]
   },
   {
    points: [ 
      { x: 0.46, y: 0.30 },
      { x: 0.46, y: 0.6 },
      { x: 0.4, y: 0.64 },
      { x: 0.52, y: 0.7 },
      { x: 0.38, y: 0.77 },
    ]
   },
  {
 points: [
    { x: 0.77, y: 0.645 },
    { x: 0.36, y: 0.85 },
    { x: 0.16, y: 0.75 },
    ]
  },
];

// Landscape mode system lines - RIGHT SIDE (renormalized to 0-1 range)
const systemLinesRight = [
  
  {
    points: [
      { x: 0.34, y: 0.71 },
  { x: 0.855, y: 0.518 },
  { x: 0.715, y: 0.468 },
  { x: 0.715, y: 0.25 },
    ]
  },
  {
    points: [
      { x: 0.672, y: 0.25 },
      { x: 0.672, y: 0.48 },
      { x: 0.775, y: 0.515 }, //cloudline 3
      { x: 0.645, y: 0.565 },
      { x: 0.53, y: 0.52 },
    ]
  },
  {
    points: [
      { x: 0.63, y: 0.25 },
      { x: 0.625, y: 0.5 },
    ]
  },
  {
    points: [
      { x: 0.73, y: 0.7 },
  { x: 0.9, y: 0.63 },
  { x: 0.725, y: 0.568 }
    ]
  },
  {
    points: [
      { x: 0.25, y: 0.57 },
  { x: 0.44, y: 0.494 },
  { x: 0.245, y: 0.42 },
  { x: 0.44, y: 0.332 },
  { x: 0.59, y: 0.388 },
  { x: 0.59, y: 0.25 }
    ]
  }
];

// Portrait mode system lines (will be adjusted for portrait aspect ratio)
const systemLinesPortrait = [
  { 
    points: [
      { x: 0.30, y: 0.15 },
      { x: 0.30, y: 0.35 },
      { x: 0.33, y: 0.40 },
      { x: 0.30, y: 0.45 },
      { x: 0.28, y: 0.42 },
    ]
  },
  { 
    points: [
      { x: 0.32, y: 0.15 },
      { x: 0.32, y: 0.33 },
      { x: 0.415, y: 0.50 },
    ]
  },
  { 
    points: [
      { x: 0.335, y: 0.15 },
      { x: 0.335, y: 0.30 },
      { x: 0.378, y: 0.38},
      { x: 0.40,  y: 0.35},
      { x: 0.425, y: 0.40},
      { x: 0.473, y: 0.33 },
      { x: 0.40, y: 0.23 },
    ]
  },
  {
    points: [ 
      { x: 0.44, y: 0.24 },
      { x: 0.373, y: 0.32 },
      { x: 0.35, y: 0.29 },
      { x: 0.35, y: 0.15 },
    ]
  },
  {
    points: [
      { x: 0.32, y: 0.52 },
      { x: 0.29, y: 0.58 },
    ]
  },
  {
    points: [
      { x: 0.33, y: 0.48 },
      { x: 0.375, y: 0.57},
      { x: 0.422, y: 0.50 },
    ]
  },
  {
    points: [
      { x: 0.357, y: 0.54 },
      { x: 0.28, y: 0.66 },
      { x: 0.22, y: 0.58 },
    ]
  },
  {
    points: [
      { x: 0.425, y: 0.50 },
      { x: 0.48, y: 0.40 },
    ]
  }
];

// Mobile-specific system lines (fresh coordinates for mobile/portrait)
const systemLinesMobile = [
  { 
    points: [
      { x: 0.335, y: 0.15 },
      { x: 0.335, y: 0.30 },
      { x: 0.378, y: 0.38},
      { x: 0.40,  y: 0.35},
      { x: 0.425, y: 0.40},
      { x: 0.473, y: 0.33 },
      { x: 0.40, y: 0.23 },
    ]
  },
  {
    points: [ 
      { x: 0.44, y: 0.24 },
      { x: 0.373, y: 0.32 },
      { x: 0.35, y: 0.29 },
      { x: 0.35, y: 0.15 },
    ]
  },
  {
    points: [
      { x: 0.32, y: 0.52 },
      { x: 0.29, y: 0.58 },
    ]
  },
  {
    points: [
      { x: 0.33, y: 0.48 },
      { x: 0.375, y: 0.57},
      { x: 0.422, y: 0.50 },
    ]
  },
  {
    points: [
      { x: 0.357, y: 0.54 },
      { x: 0.28, y: 0.66 },
      { x: 0.22, y: 0.58 },
    ]
  },
  {
    points: [
      { x: 0.425, y: 0.50 },
      { x: 0.48, y: 0.40 },
    ]
  },
];

// Portrait SVG system lines (for rearranged portrait-specific SVG)
const systemLinesPortraitSVG = [
  // Add your portrait SVG line coordinates here
];

const ServiceCarousel = ({ t }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef(null);
  const [direction, setDirection] = useState(0);
  const [tapSide, setTapSide] = useState(null);
  const theme = useTheme();
  const orientation = useOrientation();

  const services = useMemo(() => {
    return [
    {
      id: 'ivr',
      title: t('services.ivr.title'),
      description: t('services.ivr.description'),
      icon: <PhoneInTalkIcon fontSize="large" />,
      color: '#2563eb',
      bgGradient: 'linear-gradient(135deg, #0f1419 0%, #1a2850 100%)',
      content: t('services.ivr.overview'),
      features: {
        0: t('services.ivr.features.0'),
        1: t('services.ivr.features.1'),
        2: t('services.ivr.features.2'),
        3: t('services.ivr.features.3'),
        4: t('services.ivr.features.4'),
      },
      techStack: [
        'ASR (Automatic Speech Recognition)',
        'SIP/VoIP Integration',
        'Natural Language Processing',
        'DTMF Detection',
        'CRM Integration',
      ],
      stats: { projects: '50+', satisfaction: '99%', uptime: '99.9%' },
      cta: 'Explore IVR Solutions →',
      voiceWave: true, // Uses voice wave effect
    },
    {
      id: 'webdev',
      title: t('services.webDev.title'),
      description: t('services.webDev.description'),
      icon: <DevicesOtherIcon fontSize="large" />,
      color: '#0ea5e9',
      bgGradient: 'linear-gradient(135deg, #0f1419 0%, #1a3a50 100%)',
      content: t('services.webDev.overview'),
      features: [
        t('services.webDev.features.0'),
        t('services.webDev.features.1'),
        t('services.webDev.features.2'),
        t('services.webDev.features.3'),
      ],
      adaptability: t('services.webDev.adaptability'),
      techMention: t('services.webDev.techMention'),
      techStack: ['React', 'React Native', 'Flutter', 'Next.js', 'Node.js', 'AWS'],
      stats: { projects: '150+', clients: '50+', years: '20+' },
      cta: t('services.webDev.cta'),
    },
    {
      id: 'backend',
      title: t('services.backend.title'),
      description: t('services.backend.description'),
      icon: <PhoneInTalkIcon fontSize="large" />,
      color: '#10b981',
      bgGradient: 'linear-gradient(135deg, #0f1419 0%, #134e4a 100%)',
      backgroundImage: 'url(/bgs/backend-bg.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      content: t('services.backend.overview'),
      features: [
        t('services.backend.features.0'),
        t('services.backend.features.1'),
        t('services.backend.features.2'),
        t('services.backend.features.3'),
        t('services.backend.features.4'),
      ],
      credibility: t('services.backend.credibility'),
      idealFor: [
        t('services.backend.idealFor.0'),
        t('services.backend.idealFor.1'),
        t('services.backend.idealFor.2'),
      ],
      techStack: ['Node.js', 'PostgreSQL', 'Redis', 'Nginx', 'Docker', 'AWS'],
      stats: { uptime: '99.9%', systems: '50+', capacity: '1M+/sec' },
      cta: t('services.backend.cta'),
      systemLinesLeft: orientation === 'landscape' ? systemLinesLeft : [],
      systemLinesRight: orientation === 'landscape' ? systemLinesRight : [],
    },
    {
      id: 'sports',
      title: t('services.sports.title'),
      description: t('services.sports.description'),
      icon: <SportsSoccerIcon fontSize="large" />,
      color: '#eeeeee',
      bgGradient: 'linear-gradient(135deg, #0f1419 0%, #2a1a00 100%)',
      content: t('featured.sports.description'),
      features: [
        t('featured.sports.features.0'),
        t('featured.sports.features.1'),
        t('featured.sports.features.2'),
        t('featured.sports.features.3'),
        t('featured.sports.features.4'),
      ],
      techStack: [
        t('featured.sports.technologies.0'),
        t('featured.sports.technologies.1'),
        t('featured.sports.technologies.2'),
        t('featured.sports.technologies.3'),
        t('featured.sports.technologies.4'),
      ],
      stats: { dataPoints: '1M+/day', clients: '100+', latency: '<100ms' },
      cta: 'View Sports Platform →',
      ctaColor: '#1a3a50',
      specialBg: true, // Uses hyperspeed effect
    },
    {
      id: 'communication',
      title: t('services.communication.title'),
      description: t('services.communication.description'),
      icon: <CampaignIcon fontSize="large" />,
      color: '#2563eb',
      bgGradient: 'linear-gradient(135deg, #0f1419 0%, #1a2850 100%)',
      content: t('services.communication.overview'),
      features: [
        t('services.communication.features.0'),
        t('services.communication.features.1'),
        t('services.communication.features.2'),
        t('services.communication.features.3'),
        t('services.communication.features.4'),
      ],
      techStack: ['SMS APIs', 'Email Services', 'Marketing Automation', 'Analytics', 'Database'],
      stats: { messages: '100M+/month', deliveryRate: '99.8%', clients: '200+' },
    },
    {
      id: 'automation',
      title: t('services.automation.title'),
      description: t('services.automation.description'),
      icon: <SmartToyIcon fontSize="large" />,
      color: '#8b5cf6',
      bgGradient: 'linear-gradient(135deg, #0f1419 0%, #3d2a5a 100%)',
      content: t('services.automation.overview'),
      features: [
        t('services.automation.features.0'),
        t('services.automation.features.1'),
        t('services.automation.features.2'),
        t('services.automation.features.3'),
        t('services.automation.features.4'),
      ],
      techStack: ['Machine Learning', 'LLM APIs', 'Custom Training', 'Automation Frameworks', 'Vector Databases'],
      stats: { models: '50+', accuracy: '99.2%', automations: '1000+' },
    },
    ];
  }, [orientation, t]);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    }),
  };

  const paginate = (newDirection, side) => {
    setDirection(newDirection);
    setCurrentIndex((prev) => (prev + newDirection + services.length) % services.length);
    
    // Visual feedback
    if (side) {
      setTapSide(side);
      setTimeout(() => setTapSide(null), 300);
    }
  };

  const goToSlide = (index) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') paginate(-1);
      if (e.key === 'ArrowRight') paginate(1);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Wheel navigation
  useEffect(() => {
    let wheelTimeout = null;
    const handleWheel = (e) => {
      if (wheelTimeout) return;
      e.preventDefault();
      
      if (e.deltaY > 0) {
        paginate(1);
      } else if (e.deltaY < 0) {
        paginate(-1);
      }

      wheelTimeout = setTimeout(() => {
        wheelTimeout = null;
      }, 800);
    };

    containerRef.current?.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      containerRef.current?.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // Touch/Swipe navigation for mobile
  useEffect(() => {
    let touchStart = null;
    let touchTimeout = null;

    const handleTouchStart = (e) => {
      touchStart = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
      if (!touchStart || touchTimeout) return;

      const touchEnd = e.changedTouches[0].clientX;
      const delta = touchStart - touchEnd;
      const minSwipeDistance = 50;

      if (Math.abs(delta) > minSwipeDistance) {
        if (delta > 0) {
          // Swiped left, go next
          paginate(1);
        } else {
          // Swiped right, go previous
          paginate(-1);
        }

        touchTimeout = setTimeout(() => {
          touchTimeout = null;
        }, 800);
      }
    };

    containerRef.current?.addEventListener('touchstart', handleTouchStart, { passive: true });
    containerRef.current?.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      containerRef.current?.removeEventListener('touchstart', handleTouchStart);
      containerRef.current?.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const current = services[currentIndex];

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100vh',
        overflow: 'hidden',
        background: current.id === 'backend' ? '#1a4a6f' : current.bgGradient,
        backgroundImage: current.id === 'backend' ? 'none' : current.backgroundImage,
        backgroundSize: current.backgroundSize,
        backgroundPosition: current.backgroundPosition,
        backgroundAttachment: 'fixed',
        position: 'relative',
      }}
    >
      {/* Hyperspeed background for sports */}
      {current.specialBg && <HyperspeedBg intensity={0.8} />}

      {/* Animated mesh background for web dev */}
      {current.meshBg && <AnimatedMeshBg intensity={0.8} />}

      {/* Voice wave background for IVR */}
      {current.voiceWave && <VoiceWave data={current} />}

      {/* Backend Visualization Panels - unified component with SVG + canvas overlay */}
      {current.id === 'backend' && orientation === 'landscape' && (
        <Box
          dir="ltr"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 1,
            pointerEvents: 'none',
            paddingX: 2,
          }}
        >
          {/* Left visualization panel */}
          <BackendVisualizationPanel
            svgSrc={backendSystemLeftSVG}
            lines={current.systemLinesLeft || []}
            side="left"
            maxWidth="39%"
            strokeColor="#00ffff"
            strokeWidth={3.5}
            speed={75}
            opacity={0.8}
          />

          {/* Right visualization panel */}
          <BackendVisualizationPanel
            svgSrc={backendSystemRightSVG}
            lines={current.systemLinesRight || []}
            side="right"
            maxWidth="31%"
            strokeColor="#00ffff"
            strokeWidth={3.5}
            speed={75}
            opacity={0.8}
          />
        </Box>
      )}

      {/* Backend SVG system visualization - PORTRAIT */}
      {current.id === 'backend' && orientation === 'portrait' && (
        <Box
          dir="ltr"
          component="img"
          src={backendSystemPortraitSVG}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '90%',
            maxHeight: '50vh',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />
      )}




      <AnimatePresence initial={false} custom={direction} mode="wait">
        <MotionBox
          key={currentIndex}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 30 },
            opacity: { duration: 0.3 },
          }}
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <Container maxWidth="lg" sx={{ 
            position: 'relative', 
            zIndex: 10, 
            py: 4, 
            pt: { xs: 36, md: 4 },
            backgroundColor: { xs: 'rgba(10, 15, 20, 0.88)', md: 'transparent' },
            backdropFilter: { xs: 'blur(12px)', md: 'none' },
            borderRadius: 'var(--radius-xl)',
            px: { xs: 3, md: 6 },
            border: { xs: '1px solid rgba(255, 255, 255, 0.15)', md: 'none' },
          }}>
            <Stack spacing={4} alignItems="center" textAlign="center">
              {/* Service Icon */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <Box
                  sx={{
                    fontSize: '80px',
                    color: current.color,
                    display: 'flex',
                    filter: `drop-shadow(0 0 20px ${current.color})`,
                  }}
                >
                  {current.icon}
                </Box>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <Typography
                  component="h2"
                  variant="h2"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 800,
                    color: 'var(--text-light)',
                  }}
                >
                  {current.title}
                </Typography>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                    color: 'rgba(255, 255, 255, 0.85)',
                    lineHeight: 1.6,
                    fontWeight: 400,
                  }}
                >
                  {current.content}
                </Typography>
              </motion.div>
               <motion.div
                 initial={{ opacity: 0, y: 20 }}
                 animate={{ opacity: 1, y: 0 }}
                 transition={{ delay: 0.5, duration: 0.5 }}
               >
                 <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
                   {(Array.isArray(current.features) ? current.features : Object.values(current.features)).map((feature, idx) => (
                     <Chip
                       key={idx}
                       label={feature}
                       sx={{
                         backgroundColor: 'rgba(255, 255, 255, 0.1)',
                         color: 'var(--text-light)',
                         border: `1px solid ${current.color}`,
                         fontWeight: 500,
                         backdropFilter: 'blur(10px)',
                       }}
                     />
                   ))}
                 </Stack>
               </motion.div>

               {/* Adaptability or Credibility line */}
               {current.adaptability && (
                 <motion.div
                   initial={{ opacity: 0, y: 20 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ delay: 0.55, duration: 0.5 }}
                 >
                   <Typography
                     sx={{
                       fontSize: '0.95rem',
                       color: 'rgba(255, 255, 255, 0.8)',
                       fontStyle: 'italic',
                       maxWidth: '600px',
                       lineHeight: 1.7,
                     }}
                   >
                     {current.adaptability}
                   </Typography>
                 </motion.div>
               )}

               {current.credibility && (
                 <motion.div
                   initial={{ opacity: 0, y: 20 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ delay: 0.55, duration: 0.5 }}
                 >
                   <Typography
                     sx={{
                       fontSize: '0.95rem',
                       color: 'rgba(255, 255, 255, 0.8)',
                       fontStyle: 'italic',
                       maxWidth: '600px',
                       lineHeight: 1.7,
                     }}
                   >
                     {current.credibility}
                   </Typography>
                 </motion.div>
               )}

               {/* Ideal For */}
               {current.idealFor && (
                 <motion.div
                   initial={{ opacity: 0, y: 20 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ delay: 0.58, duration: 0.5 }}
                 >
                   <Box>
                     <Typography
                       sx={{
                         fontSize: '0.85rem',
                         color: 'rgba(255, 255, 255, 0.7)',
                         textTransform: 'uppercase',
                         letterSpacing: '1.5px',
                         mb: 1.5,
                         fontWeight: 700,
                       }}
                     >
                       Ideal for
                     </Typography>
                     <Stack spacing={0.8}>
                       {current.idealFor.map((item, idx) => (
                         <Typography
                           key={idx}
                           sx={{
                             fontSize: '0.9rem',
                             color: 'rgba(255, 255, 255, 0.75)',
                             pl: 2,
                             borderLeft: `2px solid ${current.color}`,
                           }}
                         >
                           {item}
                         </Typography>
                       ))}
                     </Stack>
                   </Box>
                 </motion.div>
               )}

               {/* Tech Mention or Tech Stack */}
               {current.techMention ? (
                 <motion.div
                   initial={{ opacity: 0, y: 20 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ delay: 0.6, duration: 0.5 }}
                 >
                   <Typography
                     sx={{
                       fontSize: '0.9rem',
                       color: 'rgba(255, 255, 255, 0.8)',
                       lineHeight: 1.6,
                     }}
                   >
                     {current.techMention}
                   </Typography>
                 </motion.div>
               ) : (
                 <motion.div
                   initial={{ opacity: 0, y: 20 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ delay: 0.6, duration: 0.5 }}
                 >
                   <Box>
                     <Typography
                       sx={{
                         fontSize: '0.9rem',
                         color: 'rgba(255, 255, 255, 0.7)',
                         textTransform: 'uppercase',
                         letterSpacing: '2px',
                         mb: 2,
                       }}
                     >
                       Built with
                     </Typography>
                     <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center">
                       {(Array.isArray(current.techStack) ? current.techStack : Object.values(current.techStack)).map((tech, idx) => (
                         <Typography
                           key={idx}
                           sx={{
                             fontSize: '0.95rem',
                             color: current.color,
                             fontWeight: 600,
                             opacity: 0.8,
                           }}
                         >
                           {tech}
                         </Typography>
                       ))}
                     </Stack>
                   </Box>
                 </motion.div>
               )}

               {/* CTA */}
               <motion.div
                 initial={{ opacity: 0, y: 20 }}
                 animate={{ opacity: 1, y: 0 }}
                 transition={{ delay: 0.75, duration: 0.5 }}
               >
                 <Button
                   variant="contained"
                   sx={{
                     backgroundColor: current.ctaColor || current.color,
                     color: '#ffffff',
                     fontSize: '1rem',
                     fontWeight: 700,
                     px: 4,
                     py: 1.5,
                     borderRadius: 'var(--radius-lg)',
                     textTransform: 'none',
                     boxShadow: `0 12px 30px ${(current.ctaColor || current.color)}40`,
                     transition: 'all var(--transition-base)',
                     '&:hover': {
                       transform: 'translateY(-4px)',
                       boxShadow: `0 16px 40px ${(current.ctaColor || current.color)}60`,
                     },
                   }}
                 >
                   {current.cta || 'Learn More'}
                 </Button>
               </motion.div>
            </Stack>
          </Container>
        </MotionBox>
      </AnimatePresence>

      {/* Left Edge - Clickable area with visual feedback */}
      <motion.button
        onClick={() => paginate(-1, 'left')}
        style={{
          position: 'absolute',
          insetInlineStart: 0,
          top: 0,
          width: '20%',
          height: '100%',
          background: tapSide === 'left' ? 'rgba(0, 0, 0, 0.2)' : 'transparent',
          border: 'none',
          cursor: 'pointer',
          zIndex: 5,
          padding: 0,
          transition: 'background 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          paddingInlineStart: '1rem',
        }}
        whileTap={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
      >
        {/* Left Arrow */}
        <motion.div
          style={{
            fontSize: '3rem',
            fontWeight: 300,
            color: 'rgba(255, 255, 255, 0.5)',
            transition: 'color 0.3s ease',
            opacity: tapSide === 'left' ? 1 : 0.5,
          }}
        >
          ⟨
        </motion.div>
      </motion.button>

      {/* Right Edge - Clickable area with visual feedback */}
      <motion.button
        onClick={() => paginate(1, 'right')}
        style={{
          position: 'absolute',
          insetInlineEnd: 0,
          top: 0,
          width: '20%',
          height: '100%',
          background: tapSide === 'right' ? 'rgba(0, 0, 0, 0.2)' : 'transparent',
          border: 'none',
          cursor: 'pointer',
          zIndex: 5,
          padding: 0,
          transition: 'background 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          paddingInlineEnd: '1rem',
        }}
        whileTap={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
      >
        {/* Right Arrow */}
        <motion.div
          style={{
            fontSize: '3rem',
            fontWeight: 300,
            color: 'rgba(255, 255, 255, 0.5)',
            transition: 'color 0.3s ease',
            opacity: tapSide === 'right' ? 1 : 0.5,
          }}
        >
          ⟩
        </motion.div>
      </motion.button>


      {/* Service Counter */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 20, md: 40 },
          right: { xs: 20, md: 40 },
          fontSize: '1rem',
          color: 'rgba(255, 255, 255, 0.7)',
          fontWeight: 600,
          zIndex: 10,
        }}
      >
        {currentIndex + 1} / {services.length}
      </Box>
    </Box>
  );
};

export default ServiceCarousel;
